/**
 * 后端 API 客户端。
 *
 * 开发态经 Vite 代理到 :3079，路径是相对的 `/api`，浏览器看到的是同源；
 * 线上前后端分域（前端在 Cloudflare Pages），由 VITE_API_BASE 补上 API 的源。
 * 两种情况都带 `credentials: 'include'`，所以本地与线上的 cookie 行为一致。
 */

import { API_BASE } from './config'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: number,
    readonly errorCode?: string,
    /**
     * 错误信封里的 `data`。多数错误没有它，但有几条**把要点放在这里**：
     * 冻结前置检查的 `{ cursor, endBlock, needed }`、字段锁定的键名清单。
     * 只留一句 message 的话，界面就只能把那些要点当成一段不可解析的文字重复出去。
     */
    readonly data?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api${path}`, {
    ...init,
    // 认证走 cookie，必须带上，否则每个请求都是未登录
    credentials: 'include',
    headers: {
      ...(init?.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init?.headers,
    },
  })

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      message?: string
      code?: number
      errorCode?: string
      data?: unknown
    }
    throw new ApiError(
      body.message ?? `请求失败（HTTP ${res.status}）`,
      res.status,
      body.code,
      body.errorCode,
      body.data,
    )
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>) => {
    const qs = params
      ? `?${new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => [k, String(v)]),
        )}`
      : ''
    return request<T>(`${path}${qs}`)
  },
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  postForm: <T>(path: string, body: FormData) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

export type AlertRulesSnapshot = Ok<typeof rpc.api.alerts.$get>
export type AnnouncementList = Ok<typeof rpc.api.announcements.$get>
export type AnnouncementLogs = Ok<typeof rpc.api.announcements.logs.$get>

// ── 与后端对齐的响应类型 ────────────────────────────────────────────────
//
// ★ **全部由 Hono RPC 从服务端推断，不再手写。**
//
// 以前这里是一份手抄的类型，和服务端没有任何关联 —— 2026-08 服务端把
// `netEthAmount` 改名成 `netQuoteAmount`，这里没跟着改，TypeScript 一言不发，
// 直到浏览器里 `undefined.startsWith` 崩掉。现在改服务端字段名会**编译期**报错。
//
// ⚠️ 它抓不住的那一类：`$queryRaw<TokenRow[]>` 里的类型是**断言**，
// SQL 别名和类型对不上它看不出来 —— 那只能靠跑真库的测试。

import type { InferResponseType } from 'hono/client'

import { rpc } from './rpc'

/** 分页响应外壳 */
export type Paginated<T> = { data: T[]; total: number; pageCount: number }

/**
 * ★ 一律固定到 200。
 *
 * Hono 推断出的是**所有可能响应的联合**——路由里那句
 * `return c.json({ code: 404, message: '未找到' }, 404)` 也算一支。
 * 不指定状态码的话，拿到的是 `TokenDetail | { code, message }`，
 * 任何属性访问都会报「不存在于联合类型上」。
 */
type Elem<T> = T extends { data: (infer U)[] } ? U : never

/** `InferResponseType<..., 200>` 的简写，本文件里每条都长一个样 */
type Ok<T> = InferResponseType<T, 200>

export type TokenRow = Elem<InferResponseType<typeof rpc.api.tokens.$get, 200>>
export type TokenDetail = InferResponseType<(typeof rpc.api.tokens)[':address']['$get'], 200>
export type TradeRow = Elem<InferResponseType<typeof rpc.api.trades.$get, 200>>
export type TraderRow = Elem<InferResponseType<typeof rpc.api.traders.$get, 200>>
export type WalletRow = Elem<InferResponseType<typeof rpc.api.wallets.$get, 200>>
export type UserRow = Elem<InferResponseType<typeof rpc.api.users.$get, 200>>
export type SessionRow = Elem<InferResponseType<typeof rpc.api.sessions.$get, 200>>
export type AuditRow = Elem<InferResponseType<(typeof rpc.api)['audit-logs']['$get'], 200>>
export type SystemLogRow = Elem<InferResponseType<(typeof rpc.api)['system-logs']['$get'], 200>>

export type Overview = InferResponseType<typeof rpc.api.stats.overview.$get, 200>
export type DailyPoint = Elem<InferResponseType<typeof rpc.api.stats.daily.$get, 200>>
export type TopToken = Elem<InferResponseType<(typeof rpc.api.stats)['top-tokens']['$get'], 200>>

export type HoldingRow = Elem<
  InferResponseType<(typeof rpc.api.tokens)[':address']['holders']['$get'], 200>
>
export type PricePoint = Elem<
  InferResponseType<(typeof rpc.api.tokens)[':address']['price-series']['$get'], 200>
>
export type TokenTradeStats = InferResponseType<
  (typeof rpc.api.tokens)[':address']['stats']['$get'],
  200
>

/**
 * 单币运营账本：ASP、创始人名单、分红、税转换与回购金库。
 *
 * 里面每一组金额的精度来源都不同（分红看 assetDecimals、回购收入看 revenueDecimals、
 * 买到的代币固定 18 位），所以整块都从服务端推断而不是在前端另立一份形状——
 * 少推一个 `assetDecimals` 就等于默默用错精度，那是不会报错的那类事故。
 */
export type TokenLedger = Ok<(typeof rpc.api.tokens)[':address']['ledger']['$get']>

/**
 * 当前用户 + 权限 + 可见菜单。
 *
 * ⚠️ 这个曾经是手写的，原因不是疏忽：`/api/me` 起初以独立的 `app.get(...)` 语句注册，
 * 返回值被丢弃，压根不在 `typeof routes` 里，`rpc.api.me` 不存在。
 * 全站赌注最大的一份数据（permissions 与 nav 驱动每个 useCan 和路由守卫）
 * 恰好是唯一没被守住的——已在 `server/src/index.ts` 把它链进来。
 */
export type Me = Ok<typeof rpc.api.me.$get>

export type AuthConfig = Ok<(typeof rpc.api)['auth-config']['$get']>

export type RoleMenuPolicies = Ok<typeof rpc.api.roles.$get>
export type RoleMenuDefinition = RoleMenuPolicies['menus'][number]
export type RoleMenuPolicy = RoleMenuPolicies['roles'][number]

/**
 * 交易者详情 = 列表行 + 交易过的代币明细。
 *
 * 以前前端手工拼成 `TraderRow & { tokens: TraderTokenRow[] }`，而 `TraderTokenRow`
 * 里就是 `netQuoteAmount`——**引发这次改动的那个字段**。最该被守住的类型，
 * 恰恰是当初漏掉的那个。直接取服务端的形状，不再自己拼。
 */
export type TraderDetail = Ok<(typeof rpc.api.traders)[':address']['$get']>
export type TraderTokenRow = TraderDetail['tokens'][number]

/** 单个对象的处置历史。比完整审计少了 actor 关联，权限也更宽（token:read / wallet:read） */
export type HistoryRow = Elem<Ok<(typeof rpc.api.tokens)[':address']['history']['$get']>>

export type SystemLogSummary = Ok<(typeof rpc.api)['system-logs']['summary']['$get']>

export type ContractListStatus = Ok<typeof rpc.api.contracts.lists.status.$get>
export type ContractOperationPlan = Ok<(typeof rpc.api.contracts.lists)['operation-plan']['$post']>
export type ContractOperationResult = Ok<typeof rpc.api.contracts.lists.operations.$post>
export type ContractBatchOperationResult = Ok<
  (typeof rpc.api.contracts.lists)['batch-operations']['$post']
>

/**
 * Manager 全局控制状态。
 *
 * ⚠️ 这是一个**判别联合**：`ready: false` 那一支里 owner / signer / configured
 * 全是 null，`pauses` 是空数组。别在读字段前省掉 `status.ready` 的判断——
 * 那正是「RPC 挂了却把 owner 显示成 null 当成真的没有 owner」的来源。
 */
export type ManagerControlStatus = Ok<typeof rpc.api.contracts.manager.status.$get>
export type ManagerPauseState = Extract<ManagerControlStatus, { ready: true }>['pauses'][number]
export type ManagerControlResult = Ok<typeof rpc.api.contracts.manager.operations.$post>

/**
 * 五个 Ownable2Step 合约的所有权交接状态。
 *
 * `ready: false` 时（RPC 链号不符 / 校验不可用）`contracts` 是空数组；`ready: true`
 * 时每个合约各自可能是「未配置」或「读失败」，由各自的 `configured` / `reason` 标记。
 */
export type OwnershipControlsStatus = Ok<typeof rpc.api.contracts.ownership.status.$get>
export type OwnershipContractStatus = OwnershipControlsStatus['contracts'][number]
export type OwnershipOperationResult = Ok<typeof rpc.api.contracts.ownership.operations.$post>

/**
 * 资金统计:内盘平台手续费(Manager)+ LP 已 booked 平台份额(两个 locker)。
 *
 * `ready:false` 时(RPC 链号不符 / 校验不可用)各区 rows 为空;`pricesAvailable:false`
 * 时余额照常展示但 usd 一律 null。项目币侧(isProjectToken)不折美元。
 */
export type TreasuryStatus = Ok<typeof rpc.api.treasury.status.$get>
export type TreasurySection = TreasuryStatus['platform']
export type TreasuryTokenAmount = TreasurySection['rows'][number]
export type TreasuryGraduatedRow = TreasuryStatus['lp']['graduated'][number]
export type TreasuryOperationResult = Ok<typeof rpc.api.treasury.operations.$post>

/** 资金累计（已提取 + 当前可提取）与提取记录，随 /treasury/status 一并下发。 */
export type TreasuryStats = NonNullable<TreasuryStatus['stats']>
export type TreasuryCumulativeRow = TreasuryStats['cumulative'][number]
export type TreasuryRecord = TreasuryStats['records'][number]

/** 触发后台同步的返回：{started} 或 {alreadyRunning}（立即返回，不等扫完）。 */
export type TreasurySyncTrigger = Ok<(typeof rpc.api.treasury)['sync-withdrawals']['$post']>
/** 触发「全量重扫」的返回：与增量同步同形状（清游标后从创世块重扫，幂等去重）。 */
export type TreasuryFullRescan = Ok<(typeof rpc.api.treasury)['full-rescan']['$post']>
/** 后台同步任务状态（前端轮询这个直到 done）。 */
export type TreasurySyncStatus = Ok<(typeof rpc.api.treasury)['sync-status']['$get']>
/** 同步 RPC 的脱敏状态（configured + 尾部 hint，绝不含完整 URL/key）。 */
export type TreasuryRpcConfig = Ok<(typeof rpc.api.treasury)['rpc-config']['$get']>

/** 独立资金台的会话状态：是否已设置凭据 / 当前是否已登录 / 用户名。 */
export type TreasuryConsoleSession = Ok<typeof rpc.api.treasury.session.$get>

/** 每个 meme 项目给平台产生的收入（内盘手续费,按 token 聚合）。 */
export type TokenRevenue = Ok<(typeof rpc.api.treasury)['token-revenue']['$get']>
export type TokenRevenueRow = TokenRevenue['rows'][number]

/** 税收明细：每个币的交易税拆成平台协议费 / 创作者 / 持有人三份。 */
export type TaxBreakdown = Ok<(typeof rpc.api.treasury)['tax-breakdown']['$get']>
export type TaxBreakdownRow = TaxBreakdown['rows'][number]

/** 收益总览：时间 / 项目 / 费率三个维度的聚合，供资金台「概览」页作图。 */
export type TreasuryOverview = Ok<(typeof rpc.api.treasury)['overview']['$get']>
export type TreasuryOverviewBucket = TreasuryOverview['buckets'][number]
export type TreasuryOverviewVenue = TreasuryOverview['venues'][number]
export type TreasuryOverviewToken = TreasuryOverview['topTokens'][number]
export type TreasuryOverviewRange = TreasuryOverview['range']
export type TreasuryOverviewGranularity = TreasuryOverview['granularity']

export type SystemFactoryStatus = Ok<typeof rpc.api.contracts.dividends.status.$get>
export type TaxExecutorResult = Ok<
  (typeof rpc.api.contracts.dividends)['tax-executor-operations']['$post']
>
export type LaunchVanityConfig = Ok<typeof rpc.api.contracts.vanity.config.$get>

/**
 * 代币资料修改申请（非创建者提交，待运营审核）。
 *
 * `patch` 里每个字段有三种状态，**类型上也必须是三种**：键不存在 = 不改、
 * 值为 null = 显式清空、值为字符串 = 改成它。把 null 折叠成 undefined 的话，
 * 「清空官网」在界面上会显示成「不变」——那是审错的直接来源。
 */
export type MetaProposalList = Ok<(typeof rpc.api)['meta-proposals']['$get']>
export type MetaProposalRow = MetaProposalList['items'][number]
export type MetaProposalPatch = MetaProposalRow['patch']
export type MetaProposalApproveResult = Ok<
  (typeof rpc.api)['meta-proposals'][':id']['approve']['$post']
>
export type MetaProposalRejectResult = Ok<
  (typeof rpc.api)['meta-proposals'][':id']['reject']['$post']
>
export type MetaProposalDeleteResult = Ok<
  (typeof rpc.api)['meta-proposals'][':id']['delete']['$post']
>

export type OperationsOverview = Ok<typeof rpc.api.operations.overview.$get>
export type PipelineStatus = Ok<typeof rpc.api.operations.pipelines.$get>
export type BuybackSchedulerConfig = Ok<
  (typeof rpc.api.operations)['scheduler-jobs']['buyback']['$get']
>
export type TaxDistributionPayoutConfig = Ok<
  (typeof rpc.api.operations)['scheduler-jobs']['payout-dividends']['$get']
>
export type StreamStat = OperationsOverview['streams'][number]
export type BuybackRunRow = Elem<Ok<(typeof rpc.api.operations)['buyback-runs']['$get']>>
export type SwapQuoteRow = Elem<Ok<(typeof rpc.api.operations)['swap-quotes']['$get']>>

/**
 * 写接口回传的主站生效结果。
 *
 * `warning` 非空 = 链上/数据库那一侧已经改了，但**主站缓存没能确认生效**。
 * 这类响应仍然是 HTTP 200，界面上如果只弹一句「已下架」，运营会以为万事大吉，
 * 而实际上前台还挂着那个币。所以调用方必须把 warning 单独提示出来。
 */
export type TokenOpsResponse = Ok<(typeof rpc.api.tokens)[':address']['hide']['$post']>
export type PropagationResult = TokenOpsResponse['propagation']

/**
 * Telegram 群通知。
 *
 * `TelegramStatus` 刻意**不含 bot token 明文**——服务端只回脱敏形式与
 * `tokenBroken` 标记。这不是前端的选择，是服务端就没给。
 */
export type TelegramStatus = Ok<typeof rpc.api.telegram.config.$get>
export type TelegramChat = Ok<typeof rpc.api.telegram.chats.$get>['chats'][number]
export type TelegramTemplates = Ok<typeof rpc.api.telegram.templates.$get>
export type TelegramTemplate = TelegramTemplates['templates'][number]
export type TelegramPreview = Ok<(typeof rpc.api.telegram.templates)['preview']['$post']>
export type TelegramBroadcasts = Ok<typeof rpc.api.telegram.broadcasts.$get>
export type TelegramBroadcast = TelegramBroadcasts['items'][number]
export type TelegramDelivery = TelegramBroadcast['deliveries'][number]
/** 人工重发的结果。`paused` = 撞在 Telegram 429 暂停期，已排队、由广播循环稍后发出。 */
export type TelegramRetryResult = Ok<(typeof rpc.api.telegram.broadcasts)[':id']['retry']['$post']>
export type TelegramInteractions = Ok<typeof rpc.api.telegram.interactions.$get>
export type TelegramInteraction = TelegramInteractions['items'][number]

/**
 * SuperNova 活动后台（PRD v2：一期活动一行，全部按 campaignId 寻址）。
 *
 * `campaign` 与列表都是后台**直连 ignix 只读库**读出来的，BigInt 与 Date 在服务层就已经
 * 转成字符串（`services/supernova.ts` 的 `campaignView`）。`phase` 是后台按同一条规则算的
 * 派生值，库里没有这一列。
 *
 * ⚠️ `leaderboard` 的行里有 `awardRank` 与 `estRewardUsd`，它们来自 **IGNIX 的公共榜单接口**，
 * 后台只是转发。递补名次的规则只有引擎那一份实现——后台重算会让这一页和前台公布的名单分叉。
 *
 * ⚠️ `leaderboard` 回的是 `snapshot`（整个快照头）而不是一个 id。预览与导出必须
 * 钉在同一份快照上——冻结那一刻服务端会按 kind 重新挑，两次调用可能落在不同快照上。
 */
/**
 * 全局设置。**不挂在任何一期活动上** —— 入口总开关与价格采样间隔是全站级的，
 * 三期活动共用同一份，所以路径里没有 campaignId。
 *
 * `allowedSampleIntervals` 由 IGNIX 给（采样间隔必须整除 60，格子才对得上整点），
 * 下拉框照它渲染而不是在前端再抄一份。
 */
export type SupernovaSettings = Ok<typeof rpc.api.supernova.settings.$get>['data']
export type SupernovaCampaignRow = Elem<Ok<typeof rpc.api.supernova.campaigns.$get>>
export type SupernovaOverview = Ok<(typeof rpc.api.supernova.campaigns)[':id']['overview']['$get']>
export type SupernovaCampaign = SupernovaOverview['campaign']
export type SupernovaPhase = SupernovaOverview['phase']
export type SupernovaSnapshotHead = NonNullable<SupernovaOverview['latestSnapshot']>
export type SupernovaRegistrations = Ok<
  (typeof rpc.api.supernova.campaigns)[':id']['registrations']['$get']
>
export type SupernovaRegistrationRow = SupernovaRegistrations['data'][number]
export type SupernovaTokenOverrideRow = Elem<
  Ok<(typeof rpc.api.supernova.campaigns)[':id']['token-overrides']['$get']>
>
export type SupernovaLeaderboardPage = Ok<
  (typeof rpc.api.supernova.campaigns)[':id']['leaderboard']['$get']
>
export type SupernovaLeaderboardRow = SupernovaLeaderboardPage['rows'][number]

/**
 * 奖池的解锁状态（规则 v1.6 的阶梯模式）。**IGNIX 派生、后台透传**，所以类型也从
 * 路由推出来 —— 手写一份的话，IGNIX 改了形状时界面会安安静静地少显示一个数字。
 */
export type SupernovaResolvedPrizePool = NonNullable<SupernovaOverview['resolvedPrizePool']>
export type SupernovaUnlock = NonNullable<SupernovaResolvedPrizePool['unlock']>

/** 合作方报名同步的状态面板读的那一份（phase 14 §2.4）。 */
export type SupernovaPartnerSync = Ok<
  (typeof rpc.api.supernova.campaigns)[':id']['partner-sync']['$get']
>['data']

export type SupernovaTokenCandidates = Ok<
  (typeof rpc.api.supernova.campaigns)[':id']['token-candidates']['$get']
>

/** 核验后的多签收支账本。 */
export type TreasuryLedger = Ok<(typeof rpc.api.treasury)['ledger']['$get']>
export type TreasuryExpenseSummary = Ok<(typeof rpc.api.treasury)['expenses']['summary']['$get']>
export type TreasuryExpenseFill = Ok<(typeof rpc.api.treasury)['expenses']['fill-prices']['$post']>

export type TreasuryExpenseNotes = Ok<(typeof rpc.api.treasury)['expenses']['notes']['$get']>

export type TreasuryProductIncome = Ok<(typeof rpc.api.treasury)['product-income']['$get']>
